import React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";

export const dashboardSafeCardSx = {
    borderRadius: "20px",
    boxShadow:
        "0 8px 32px rgba(255, 144, 21, 0.05), 0 4px 20px rgba(15, 23, 42, 0.07)",
    border: "1px solid rgba(255, 255, 255, 0.50)",
    background:
        "linear-gradient(160deg, rgba(255, 255, 255, 0.78) 0%, rgba(255, 255, 255, 0.55) 50%, rgba(245, 241, 234, 0.50) 100%)",
    backdropFilter: "blur(18px) saturate(1.5)",
    WebkitBackdropFilter: "blur(18px) saturate(1.5)",
    height: "100%",
    overflow: "hidden",
    position: "relative",
    transition:
        "transform 220ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 240ms cubic-bezier(0.4, 0, 0.2, 1), border-color 220ms cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
        transform: "translateY(-4px) scale(1.008)",
        boxShadow:
            "0 20px 56px rgba(255, 144, 21, 0.10), 0 8px 24px rgba(15, 23, 42, 0.10)",
        borderColor: "rgba(255, 144, 21, 0.22)",
    },
    "&:active": {
        transform: "translateY(-1px) scale(0.998)",
    },
};

export function DashboardSafeCard({
    icon,
    title,
    children,
    footer,
    accent = "#2563eb",
    accentSoft = "rgba(37, 99, 235, 0.14)",
}) {
    return (
        <Card sx={dashboardSafeCardSx}>
            {/* Ambient radial glow overlay */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: `
                        radial-gradient(ellipse at 8% 8%, ${accentSoft}, transparent 45%),
                        radial-gradient(ellipse at 92% 8%, rgba(255, 144, 21, 0.04), transparent 40%),
                        radial-gradient(ellipse at 50% 100%, rgba(245, 241, 234, 0.15), transparent 50%)
                    `,
                    pointerEvents: "none",
                    transition: "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                }}
            />
            {/* Top accent bar with glow */}
            <Box
                sx={{
                    height: 4,
                    background: `linear-gradient(90deg, transparent 5%, ${accent} 30%, rgba(255, 193, 0, 0.6) 50%, ${accent} 70%, transparent 95%)`,
                    opacity: 0.75,
                    transition: "opacity 220ms cubic-bezier(0.4, 0, 0.2, 1)",
                    ".MuiCard-root:hover &": {
                        opacity: 1,
                    },
                }}
            />
            <CardContent sx={{ p: { xs: 2, md: 3 }, position: "relative" }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: `linear-gradient(145deg, ${accentSoft}, rgba(255, 255, 255, 0.5))`,
                            color: accent,
                            boxShadow: `0 6px 18px ${accentSoft}, inset 0 1px 1px rgba(255, 255, 255, 0.6)`,
                            border: `1px solid rgba(255, 255, 255, 0.45)`,
                            backdropFilter: "blur(8px)",
                            WebkitBackdropFilter: "blur(8px)",
                            transition:
                                "transform 220ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 220ms cubic-bezier(0.4, 0, 0.2, 1)",
                            ".MuiCard-root:hover &": {
                                transform: "scale(1.06)",
                                boxShadow: `0 8px 24px ${accentSoft}`,
                            },
                        }}
                    >
                        {icon}
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            letterSpacing: "-0.02em",
                            color: "rgba(20, 24, 44, 0.88)",
                        }}
                    >
                        {title}
                    </Typography>
                </Stack>
                <Divider
                    sx={{
                        my: 2,
                        borderColor: "rgba(148, 163, 184, 0.15)",
                        borderWidth: "0.5px",
                    }}
                />
                {children}
                {footer}
            </CardContent>
        </Card>
    );
}
